import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from 'yup';
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetTeachersQuery } from "../../hooks/useGetTeachersQuery";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import { routes } from "../../routes";
import { getAssessments } from "../../services/school/assessment/getAssessments";
import { getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { getStudentsFromClass } from "../../services/school/student/getStudentsFromClass";
import { getTeacherClasses, getTeacherSubjects } from "../../services/school/teacher/getTeachers";
import { updateStudentAssessment_blockOrUnblock } from "../../services/school/teacher/updateStudentAssessment";
import { TeacherType } from "../../types/Teacher";
import { BlockOrUnblockStudentGradePostingForm } from "../Teachers/BlockOrUnblockStudentGradePostingForm";

export const blockOrUnblockStudentGradePostingSchema = yup.object().shape({

    idSubject: yup.string().required('Disciplina é obrigatória'),
    idClass: yup.string().required('Turma é obrigatória'),
    idAssessment: yup.string().required('Tipo de avaliação é obrigatória'),
    studentAssessment: yup.array().of(
        yup.object().shape({
            idStudent: yup.string().required('ID do estudante é obrigatório'),
            assessment: yup.object().shape({
                idAssessment: yup.string().required('ID da avaliação é obrigatório'),
                isBlocked: yup.boolean().required('isBlocked é obrigatório'),
            }),
        })
    ),
    idFaculty: yup.string().required('Unidade orgânica é obrigatória'),
    idUniversity: yup.string().required('Instituição de Ensino Superior é obrigatória'),
    idDepartament: yup.string().required('Departamento é obrigatório'),
    idTeacher: yup.string().required('Docente é obrigatório'),
});

export const BlockOrUnblockStudentGradePosting = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm({
        resolver: yupResolver(blockOrUnblockStudentGradePostingSchema),
        defaultValues: { idClass: "", idSubject: "", idAssessment: '', studentAssessment: [] },
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });

    const mutationUpdateStudentAssessment = useMutation({ mutationFn: updateStudentAssessment_blockOrUnblock, mutationKey: "updateStudentAssessment" })
    const loadingState = mutationUpdateStudentAssessment.isLoading;

    const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties, } = useGetFacultiesQuery(
        { PageNumber: 1, PageSize: 400, IdUniversity: getValues("idUniversity"), });

    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments, } = useGetDepartamentsQuery(
        { PageNumber: 1, PageSize: 400, IdFaculty: getValues("idFaculty") },
        { enabled: !!getValues("idFaculty") }
    );

    const { data: teachersData, isLoading: isLoadingTeacherData, isError: isErrorTeacherData, refetch: refreshTeachersData } = useGetTeachersQuery(
        { PageNumber: 1, PageSize: 400, IdDepartament: getValues('idDepartament') },
        { enabled: !!getValues('idDepartament') }
    );

    const [sortedTeachers, setSortedTeachers] = useState<TeacherType[] | null>(null);
    useEffect(() => {
        if (teachersData) {
            const sortedTeachersData = [...teachersData.items].sort((a, b) =>
                (a.fullname || "").localeCompare(b.fullname || "")
            );

            setSortedTeachers(sortedTeachersData);
        }
    }, [teachersData]);

    const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
        ['fetchTeacherClasses'],
        async () => {
            try {
                const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 400, IdTeacher: getValues('idTeacher') });
                return classes;
            } catch (error) {
            }
        },
        {
            enabled: !!getValues('idTeacher'),
        }
    );

    const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
        ['fetchTeacherSubjects'],
        async () => {
            try {
                const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 400, IdTeacher: getValues('idTeacher'), IdClass: getValues('idClass') });
                return subjects;
            } catch (error) {
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );

    const { isLoading: isLoadingAssessments, refetch: fetchTeacherAssessments, data: assessments, isError: isErrorTeacherAssessments } = useQuery(
        ['fetchTeacherAssessments'],
        async () => {
            try {
                const assessments = await getAssessments({ PageNumber: 1, PageSize: 400, IdTeacher: getValues('idTeacher'), IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
                return assessments;
            } catch (error) {
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );

    const { isLoading: isLoadingStudentsFromClass, refetch: fetchStudentsFromClass, data: students, isError: isErrorStudentsFromClass } = useQuery(
        ['fetchStudentsFromClass'],
        async () => {
            try {
                const students = await getStudentsFromClass({ PageNumber: 1, PageSize: 400, IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
                return students;
            } catch (error) {
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );

    const { isLoading: isLoadingStudentAssessments, refetch: fetchStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments } = useQuery(
        ['fetchStudentAssessments'],
        async () => {
            try {
                const studentsAssessments = await getStudentsAssessments({ PageNumber: 1, PageSize: 400, IdClass: getValues('idClass'), IdSubject: getValues('idSubject'), IdTeacher: getValues('idTeacher'), IdAssessment: watch('idAssessment') });
                return studentsAssessments;
            } catch (error) {
            }
        },
        {
            enabled: !!watch('idAssessment'),
            refetchOnWindowFocus: true,
            retry: true
        }
    );

    const watchAssessment = watch('idAssessment');
    const assessment = assessments?.items.find(assessment => assessment.id === watchAssessment);

const mapStudentsAssessment = (idStudent?: string) => {

        const existingStudentAssessment = studentAssessments?.items.find(
            (sa) => sa.idStudent === idStudent && sa.idAssessment === assessment?.id
        ) as any;

        return existingStudentAssessment
            ? {
                ...existingStudentAssessment,
            }
            : {
                idAssessment: assessment?.id!,
                idStudent: idStudent!,
                isBlocked: false
            };
    };

    useEffect(() => {

        if (students?.items) {

            setValue(
                'studentAssessment',
                [...students.items].sort((a, b) =>
                    (a.student || "").localeCompare(b.student || "")
                ).map((student) => ({
                    idStudent: student.idStudent,
                    assessment: mapStudentsAssessment(student.idStudent),
                }))
            );
        }
    }, [students?.items, assessment, studentAssessments?.items, setValue]);

    const handleUniversityChange = (id: string) => {
        setValue("idUniversity", id);
        setValue("idFaculty", "");
        setValue("idDepartament", "");
        refetchFaculties();
    };
    const handleFacultyChange = (idFaculty: string) => {
        setValue("idFaculty", idFaculty);
        refreshDepartaments();
    };

    const handleDepartamentChange = (idDepartament: string) => {
        setValue("idDepartament", idDepartament);
        refreshTeachersData();
    };

    const handleTeacherChange = (idTeacher: string) => {
        setValue("idTeacher", idTeacher);
        fetchTeacherClasses();
    };

    const handleChangeClass = (idClass: string) => {
        setValue('idClass', idClass);
        fetchTeacherSubjects();
    };

    const handleChangeSubject = (idSubject: string) => {
        setValue('idSubject', idSubject);
        fetchTeacherAssessments();
    };

    const handleChangeAssessment = (idAssessment: string) => {
        setValue('idAssessment', '');
        setValue('idAssessment', idAssessment);
        fetchStudentsFromClass();
        fetchStudentAssessments();
    };

    const onSubmit = handleSubmit(async (data) => {

        const mappedDataAssessments = mapStudentAssessment(data as any)

        try {
            const assessmentsUpdate = mappedDataAssessments.filter(mda => mda?.id);

            if (assessmentsUpdate.length > 0) {
                await Promise.all(
                    assessmentsUpdate.map(assessment =>
                        mutationUpdateStudentAssessment.mutateAsync(assessment)
                    )
                );
            }

            toast.success("Estado das notas actualizado com sucesso!");
            queryClient.invalidateQueries('fetchStudentsFromClass');
            queryClient.invalidateQueries('fetchTeacherAssessments');
            queryClient.invalidateQueries('fetchStudentAssessments');
            await fetchTeacherAssessments();
            await fetchStudentsFromClass();
            await fetchStudentAssessments();

        } catch (err: any) {
            toast.error(`${err?.message}`);
        }
    });

    const mapStudentAssessment = (data: { studentAssessment: any[]; }) => {
        const mappedData: { id?: any; isBlocked: any }[] = [];

        data.studentAssessment.forEach((student: { assessment: any; idStudent: any; }) => {

            const _assessment = student.assessment;

            mappedData.push({
                id: _assessment.id,
                isBlocked: _assessment.isBlocked
            });
        });
        return mappedData;
    };

    const handleBack = () => {
        navigate(routes.dashboard)
    }

    return (
        <Card>
            <Flex direction='column' gap='4'>
                <form onSubmit={onSubmit} noValidate>
                    <Flex gap='3' direction='column'>

                        <Card size='1'>
                            <Flex direction='column' gap="2">
                                <Flex gap="3">
                                    <SelectComponent
                                        name="idUniversity"
                                        label="Instituição de Ensino Superior"
                                        control={control}
                                        errors={errors}
                                        style={{ width: "100%" }}
                                        options={universities?.items.map((u) => ({
                                            value: u.id ?? "",
                                            label: u.description ?? "",
                                        }))}
                                        itemsLoading={isLoadingUniversities}
                                        itemsError={errorUniversities}
                                        onSelect={handleUniversityChange}
                                        required={true}
                                    />
                                    <SelectComponent
                                        name="idFaculty"
                                        label="Unidade orgânica"
                                        control={control}
                                        errors={errors}
                                        style={{ width: "100%" }}
                                        options={faculties?.items.map((faculty) => ({
                                            value: faculty.id ?? "",
                                            label: faculty.description ?? "",
                                        }))}
                                        onSelect={handleFacultyChange}
                                    />
                                    <SelectComponent
                                        name="idDepartament"
                                        label="Departamento"
                                        control={control}
                                        errors={errors}
                                        style={{ width: "100%" }}
                                        options={departaments?.items.map((departament) => ({
                                            value: departament.id ?? "",
                                            label: departament.description ?? "",
                                        }))}
                                        onSelect={handleDepartamentChange}
                                    />
                                </Flex>

                                <Flex gap="3">
                                    <SelectComponent
                                        label="Docente"
                                        options={sortedTeachers?.map((teacher) => ({
                                            value: teacher.id ?? "",
                                            label: `${teacher.fullname} ${teacher.teacherUniversalCode}`
                                        }))}
                                        onSelect={handleTeacherChange}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        control={control}
                                        name={"idTeacher"}
                                        required={true}
                                    />
                                </Flex>

                                <Flex gap='3'>
                                    <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                                        options={classes?.items.map(_class => {
                                            return {
                                                label: _class.description,
                                                value: _class.id ?? ""
                                            }
                                        })}
                                        onSelect={handleChangeClass}
                                        itemsLoading={isLoadingClasses}
                                        itemsError={isErrorTeacherClasses}
                                    />
                                    <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                                        options={subjects?.items.map(subject => {
                                            return {
                                                label: subject.description,
                                                value: subject.id ?? ""
                                            }
                                        })}
                                        itemsLoading={isLoadingSubjects}
                                        itemsError={isErrorTeacherSubjects}
                                        onSelect={handleChangeSubject}
                                    />
                                    <SelectComponent
                                        name='idAssessment'
                                        label='Avaliação'
                                        control={control}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        options={assessments?.items.map(assessment => ({
                                            label: assessment.type ?? '',
                                            value: assessment.id ?? ''
                                        }))}
                                        itemsLoading={isLoadingAssessments}
                                        itemsError={isErrorTeacherAssessments}
                                        onSelect={handleChangeAssessment}
                                    />
                                </Flex>
                            </Flex>
                        </Card>

                        {assessment && students?.items && (
                            <BlockOrUnblockStudentGradePostingForm
                                students={[...students?.items || []].sort((a, b) =>
                                    (a.student || "").localeCompare(b.student || "")
                                )}
                                assessment={assessment}
                                control={control}
                                setValue={setValue}
                                getValues={getValues}
                                watch={watch}
                                errors={errors}
                            />
                        )}

                        <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                            <Button type='button' variant='soft' color='gray'
                                disabled={loadingState}
                                onClick={() => {
                                    handleBack();
                                }}
                            >
                                <ChevronLeftIcon /> Voltar
                            </Button>

                            <Button type='submit' variant='surface'
                                disabled={loadingState}

                            >
                                {loadingState ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                Guardar
                            </Button>
                        </Flex>
                    </Flex>
                </form>
            </Flex>
        </Card>
    )
}